/**
 * Laboratory constants — the "Type" taxonomy shared by the Lab Catalog form and
 * the technician queue.
 *
 * A lab test's `specimen_type` covers BOTH pathology samples (Blood, Urine…) and
 * imaging modalities (X-ray, CT…). The technician's wording must differ between
 * the two — a pathology test has a sample "collected", an imaging test is
 * "scanned" — so the imaging set lives here as the single source of truth that
 * both the catalog and the queue read (see `isImagingType`).
 */

// Built-in pathology specimen types.
export const SPECIMEN_TYPES = [
  'Blood', 'Serum', 'Plasma', 'Urine', 'Stool', 'Sputum', 'Swab',
  'Tissue / Biopsy', 'Body Fluid', 'CSF', 'Semen', 'Pus', 'Bone Marrow',
];

// Built-in imaging / modality types.
export const IMAGING_TYPES = ['X-ray', 'Ultrasound (USG)', 'CT Scan', 'MRI', 'ECG', 'Mammography'];

// Both built-in groups together (the catalog's known-type set; anything else an
// org enters via "Other" self-builds into a per-org "Custom" group).
export const BUILTIN_TYPES = [...SPECIMEN_TYPES, ...IMAGING_TYPES];

const _IMAGING_LC = new Set(IMAGING_TYPES.map((t) => t.toLowerCase()));

/**
 * True when a test's `specimen_type` is an imaging modality (so the technician
 * UI says "Perform scan / Scan done" instead of "Collect sample / Sample
 * collected"). Case-insensitive; a custom (non-built-in) type is treated as a
 * pathology sample.
 * @param {string} [specimenType]
 * @returns {boolean}
 */
export const isImagingType = (specimenType) =>
  !!specimenType && _IMAGING_LC.has(String(specimenType).trim().toLowerCase());

/**
 * The lab-queue token for an order — single source of truth for the `#L-N`
 * label and the lane sort key.
 *
 * Prefer `lab_queue_number`: a unique, monotonic, per-org/per-day token stamped
 * when the order ENTERS the lab queue. Fall back to the advising visit's
 * `queue_number` only for legacy rows not yet backfilled (it is per-doctor/
 * per-day and NOT unique — that's the bug this replaces, so it's a last resort).
 * @param {object} order
 * @returns {number|null}
 */
export const labQueueToken = (order) =>
  order?.lab_queue_number ?? order?.queue_number ?? null;

/**
 * When the order entered the lab queue — drives the "Arrived"/"Waiting" display
 * so it reflects lab arrival, not the (often hours-earlier) advise time. Falls
 * back to `created_at` for legacy rows.
 * @param {object} order
 * @returns {string|undefined}
 */
export const labEntryAt = (order) => order?.lab_queued_at ?? order?.created_at;

/**
 * Canonical lab-order status metadata — one source of truth for the human label
 * + chip colours, consumed by PatientHistory, LabAdvisePanel, the lab queue
 * cards, etc.
 *
 * `standing` is the repeatable-parent status: an inert "No expiry till <review
 * date>" order the front desk performs again and again. It never enters the
 * technician's queue.
 */
export const LAB_STATUS = {
  advised: { label: 'Advised', bg: '#fef3c7', fg: '#92400e', darkBg: 'rgba(245,158,11,0.25)', darkFg: '#fcd34d' },
  standing: { label: 'Standing', bg: '#ede9fe', fg: '#6d28d9', darkBg: 'rgba(139,92,246,0.25)', darkFg: '#c4b5fd' },
  awaiting_payment: { label: 'Awaiting payment', bg: '#fef3c7', fg: '#92400e', darkBg: 'rgba(245,158,11,0.25)', darkFg: '#fcd34d' },
  payment_cleared: { label: 'Ready', bg: '#ccfbf1', fg: '#115e59', darkBg: 'rgba(13,148,136,0.25)', darkFg: '#5eead4' },
  scheduled: { label: 'Scheduled', bg: '#e0f2fe', fg: '#075985', darkBg: 'rgba(14,165,233,0.25)', darkFg: '#7dd3fc' },
  sample_collected: { label: 'Collected', bg: '#e0f2fe', fg: '#075985', darkBg: 'rgba(14,165,233,0.25)', darkFg: '#7dd3fc' },
  in_progress: { label: 'In progress', bg: '#dbeafe', fg: '#1e40af', darkBg: 'rgba(37,99,235,0.3)', darkFg: '#93c5fd' },
  completed: { label: 'Completed', bg: '#d1fae5', fg: '#065f46', darkBg: 'rgba(16,185,129,0.3)', darkFg: '#6ee7b7' },
  paid: { label: 'Completed', bg: '#d1fae5', fg: '#065f46', darkBg: 'rgba(16,185,129,0.3)', darkFg: '#6ee7b7' },
  cancelled: { label: 'Cancelled', bg: '#f3f4f6', fg: '#4b5563', darkBg: 'rgba(107,114,128,0.3)', darkFg: '#d1d5db' },
};

export function labStatusLabel(status) {
  return LAB_STATUS[status]?.label || (status ? String(status) : '');
}

// True for a standing-order parent (never a technician work item).
export function isStandingLabOrder(order) {
  return !!order && (order.standing === true || order.status === 'standing');
}
